import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarOff, Send, ThumbsUp, ThumbsDown, CheckCircle2, XCircle, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
const input = "mt-1 w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-3 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70";
const label = "text-[10px] tracking-[0.18em] uppercase text-slate-500";
const fmtDate = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || ""); return m ? `${m[3]}-${m[2]}-${m[1]}` : d || "—"; };
const fmtAt = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS = {
  pending: { label: "Pending", cls: "text-amber-300 border-amber-500/50" },
  recommended: { label: "Recommended by HR", cls: "text-sky-300 border-sky-500/50" },
  approved: { label: "Approved", cls: "text-emerald-300 border-emerald-500/50" },
  rejected: { label: "Rejected", cls: "text-red-300 border-red-500/50" },
};
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const openAttachment = async (leaveId, att) => {
  try {
    const res = await api.get(`/leave/${leaveId}/attachments/${att.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const w = window.open(url, "_blank", "noopener");
    if (!w) { const a = document.createElement("a"); a.href = url; a.download = att.name; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Could not open the attachment"); }
};
const Attachments = ({ leaveId, items }) => (items?.length ? (
  <div className="flex flex-wrap gap-2" data-testid={`leave-attachments-${leaveId}`}>
    {items.map((a) => (
      <button key={a.id} type="button" onClick={() => openAttachment(leaveId, a)} className="inline-flex items-center gap-1.5 text-[11px] text-nx-goldlight border border-nx-gold/40 hover:bg-nx-gold/10 px-2.5 py-1 max-w-full" title={a.name} data-testid={`leave-attachment-${a.id}`}>
        <FileText size={12} className="shrink-0" /> <span className="truncate max-w-[220px]">{a.name}</span> <span className="text-slate-500">{fmtSize(a.size)}</span>
      </button>
    ))}
  </div>
) : null);
const Badge = ({ status, id }) => <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${STATUS[status]?.cls || ""}`} data-testid={`leave-status-${id}`}>{STATUS[status]?.label || status}</span>;

const ApplyForm = ({ onSubmitted }) => {
  const [types, setTypes] = useState(["Casual Leave", "Sick Leave", "Earned Leave", "Compensatory Off", "Maternity Leave", "Paternity Leave", "Bereavement Leave", "Special Leave", "Leave Without Pay"]);
  const [form, setForm] = useState({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const ok = picked.filter((f) => f.size <= 5 * 1024 * 1024);
    if (ok.length < picked.length) toast.error("Files over 5 MB were skipped.");
    setFiles((prev) => [...prev, ...ok].slice(0, 5));
    e.target.value = "";
  };
  useEffect(() => { api.get("/leave/types").then(({ data }) => setTypes(data.types)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post("/leave", fd);
      toast.success(files.length ? `Leave request submitted with ${files.length} document${files.length === 1 ? "" : "s"}` : "Leave request submitted");
      setForm({ leave_type: "Sick Leave", from_date: "", to_date: "", reason: "" });
      setFiles([]);
      onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit leave request");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-5 sm:p-6 flex flex-col gap-4" data-testid="leave-apply-form">
      <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><CalendarOff size={13} className="text-nx-goldlight" /> Apply for Leave</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block"><span className={label}>Leave Type</span>
          <select required value={form.leave_type} onChange={set("leave_type")} className={input} data-testid="leave-type">
            {types.map((t) => <option key={t} value={t} className="text-neutral-900">{t}</option>)}
          </select></label>
        <label className="block"><span className={label}>From Date</span><input required type="date" value={form.from_date} onChange={set("from_date")} className={input} data-testid="leave-from" /></label>
        <label className="block"><span className={label}>To Date</span><input required type="date" min={form.from_date || undefined} value={form.to_date} onChange={set("to_date")} className={input} data-testid="leave-to" /></label>
      </div>
      <label className="block"><span className={label}>Reason</span><textarea required rows={3} maxLength={1500} value={form.reason} onChange={set("reason")} placeholder="Briefly describe the reason for leave" className={`${input} resize-y`} data-testid="leave-reason" /></label>
      <div className="block" data-testid="leave-attach-block">
        <span className={label}>Supporting documents (optional — e.g. medical certificate)</span>
        <label className="mt-1 flex items-center gap-2 border border-dashed border-white/20 hover:border-nx-gold/50 px-3 py-2.5 text-sm text-slate-300 cursor-pointer w-fit">
          <Paperclip size={14} className="text-nx-goldlight" /> Attach document{files.length ? ` (${files.length}/5)` : "s"}
          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={addFiles} className="hidden" data-testid="leave-files" />
        </label>
        <p className="text-[11px] text-slate-600 mt-1">PDF, JPG, PNG, WEBP, DOC or DOCX · up to 5 files · 5 MB each</p>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="leave-files-selected">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-slate-200 border border-white/15 px-2.5 py-1" data-testid={`leave-file-${i}`}>
                <FileText size={12} className="text-nx-goldlight" /> <span className="truncate max-w-[200px]">{f.name}</span> <span className="text-slate-500">{fmtSize(f.size)}</span>
                <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-300 ml-1" aria-label="Remove" data-testid={`leave-file-remove-${i}`}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
      <button type="submit" disabled={busy} className="self-start inline-flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60" data-testid="leave-submit"><Send size={13} /> {busy ? "Submitting…" : "Submit Request"}</button>
    </form>
  );
};

const MyLeaves = ({ items }) => (
  <div className="flex flex-col gap-3" data-testid="my-leave-list">
    <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">My leave requests ({items.length})</p>
    {items.length === 0 && <p className="text-sm text-slate-500" data-testid="my-leave-empty">No leave requests yet.</p>}
    {items.map((l) => (
      <div key={l.id} className="border border-white/10 bg-white/[0.02] p-4 sm:p-5 flex flex-col gap-2" data-testid={`my-leave-${l.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white font-medium">{l.leave_type}</span>
          <span className="text-xs text-slate-400">{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</span>
          <span className="ml-auto"><Badge status={l.status} id={l.id} /></span>
        </div>
        <p className="text-sm text-slate-300">{l.reason}</p>
        <Attachments leaveId={l.id} items={l.attachments} />
        <p className={`text-xs ${l.status === "approved" ? "text-emerald-300" : l.status === "rejected" ? "text-red-300" : "text-slate-400"}`} data-testid={`my-leave-status-text-${l.id}`}>{l.status_text}</p>
        {l.remarks.map((r, i) => <p key={i} className="text-xs text-slate-400 border-l-2 border-nx-gold/40 pl-3" data-testid={`my-leave-remark-${l.id}-${i}`}><span className="text-nx-goldlight">Remark from {r.from}:</span> {r.text}</p>)}
        <p className="text-[11px] text-slate-600">Applied {fmtAt(l.created_at)}</p>
      </div>
    ))}
  </div>
);

const ActionBox = ({ l, mode, onDone }) => {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (action) => {
    setBusy(action);
    try {
      await api.post(`/leave/${l.id}/${mode === "hr" ? "recommend" : "decide"}`, { action, remark });
      toast.success(mode === "hr" ? `Recommendation recorded (${action === "approve" ? "approval" : "rejection"})` : `Leave ${action === "approve" ? "approved" : "rejected"}`);
      setRemark("");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end" data-testid={`leave-actions-${l.id}`}>
      <label className="block flex-1"><span className={label}>{mode === "hr" ? "HR remark (optional)" : "Remark (optional)"}</span>
        <input maxLength={1000} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder={mode === "hr" ? "Note for the CEO" : "Note for the record"} className={input} data-testid={`leave-remark-${l.id}`} /></label>
      <div className="flex gap-2">
        <button onClick={() => act("approve")} disabled={!!busy} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/50 hover:bg-emerald-500/10 px-3 py-2.5 disabled:opacity-50" data-testid={`leave-approve-${l.id}`}>
          {mode === "hr" ? <ThumbsUp size={13} /> : <CheckCircle2 size={13} />} {mode === "hr" ? "Recommend Approval" : "Approve"}
        </button>
        <button onClick={() => act("reject")} disabled={!!busy} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-red-300 border border-red-500/50 hover:bg-red-500/10 px-3 py-2.5 disabled:opacity-50" data-testid={`leave-reject-${l.id}`}>
          {mode === "hr" ? <ThumbsDown size={13} /> : <XCircle size={13} />} {mode === "hr" ? "Recommend Rejection" : "Reject"}
        </button>
      </div>
    </div>
  );
};

const AllLeaves = ({ items, mode, onDone }) => (
  <div className="flex flex-col gap-3" data-testid="all-leave-list">
    <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">{mode === "hr" ? "All staff leave requests — HR recommendation" : "All staff leave requests — CEO decision"} ({items.length})</p>
    {items.length === 0 && <p className="text-sm text-slate-500">No leave requests from staff yet.</p>}
    {items.map((l) => (
      <div key={l.id} className="border border-white/10 bg-white/[0.02] p-4 sm:p-5 flex flex-col gap-3" data-testid={`leave-row-${l.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white font-medium">{l.name}</span>
          <span className="text-xs text-slate-500">{l.email}</span>
          <span className="text-[10px] tracking-[0.14em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{l.leave_type}</span>
          <span className="text-xs text-slate-400">{fmtDate(l.from_date)} → {fmtDate(l.to_date)} · {l.days} day{l.days === 1 ? "" : "s"}</span>
          <span className="ml-auto"><Badge status={l.status} id={l.id} /></span>
        </div>
        <p className="text-sm text-slate-300">{l.reason}</p>
        <Attachments leaveId={l.id} items={l.attachments} />
        {l.hr_recommendation && (
          <p className="text-xs border-l-2 border-sky-400/60 pl-3 text-slate-300" data-testid={`leave-hr-rec-${l.id}`}>
            <span className="text-sky-300">HR recommends {l.hr_recommendation.action === "approve" ? "approval" : "rejection"}</span> · {l.hr_recommendation.by} · {fmtAt(l.hr_recommendation.at)}{l.hr_recommendation.remark ? ` — ${l.hr_recommendation.remark}` : ""}
          </p>
        )}
        {l.decision && (
          <p className="text-xs border-l-2 border-emerald-400/60 pl-3 text-slate-300" data-testid={`leave-decision-${l.id}`}>
            <span className={l.decision.action === "approve" ? "text-emerald-300" : "text-red-300"}>{l.decision.action === "approve" ? "Approved" : "Rejected"} by CEO</span> · {fmtAt(l.decision.at)}{l.decision.remark ? ` — ${l.decision.remark}` : ""}
          </p>
        )}
        {!l.decision && (mode === "ceo" || !l.hr_recommendation) && <ActionBox l={l} mode={mode} onDone={onDone} />}
        {!l.decision && mode === "hr" && l.hr_recommendation && <p className="text-[11px] text-slate-500">Recommendation sent — awaiting CEO decision.</p>}
      </div>
    ))}
  </div>
);

export default function AdminLeave({ user, onAuthFail }) {
  const isCeo = (user?.email || "").toLowerCase() === "ceo@nexsecure.in" || user?.role === "ceo";
  const isHr = user?.role === "hr";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leave/mine");
      setMine(data);
      if (isCeo || isHr) setAll((await api.get("/leave")).data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
    }
  }, [isCeo, isHr, onAuthFail]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="flex flex-col gap-8" data-testid="admin-leave">
      {(isCeo || isHr) && <AllLeaves items={all} mode={isCeo ? "ceo" : "hr"} onDone={load} />}
      <ApplyForm onSubmitted={load} />
      <MyLeaves items={mine} />
    </div>
  );
}
